import { useState, useCallback } from 'react';
import { format, isBefore, parseISO } from 'date-fns';
import { CloudDownload } from '@mui/icons-material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import { fullName } from '../../../lib/utils';
import BoletoDialog from '../../../components/dialogs/BoletoDialog';
import {
    useToggle,
    useTuples,
    useCrud,
    useInteractionsFiles,
} from '../../../lib/hooks';
import {
    SttTable,
    SttTopControls,
    SttLabelPayment,
} from '../../../sporttia/all';
import { getPaymentForm } from '../../../lib/paymentHelper';
import { PERMISSIONS } from '../../../lib/permissions';

type PaymentFormType =
    | ''
    | 'DATAFONO'
    | 'DATAFONO_CONNECTED'
    | 'CASH'
    | 'PURSE'
    | 'TPV'
    | 'LATER'
    | 'BANK'
    | 'FREE'
    | 'RECEIPT'
    | 'ZITYCARD'
    | 'CASHDRO';

type User = {
    id: number;
    name: string;
    surname1?: string;
    surname2?: string;
    fullName?: string;
    mship: {
        id: number;
        name: string;
    };
};

type Payment = {
    id: number;
    price: number;
    paymentForm: PaymentFormType;
    trash: boolean;
};

type BonoData = {
    id: number | 'create';
    name: string;
    dateend: string;
    timeend: string;
    price?: number;
};

type BoletoItem = {
    id?: number;
    name: string;
    created?: string;
    user: User;
    consumed: number;
    amount: number;
    expiration: string;
    notes?: string;
    bono: BonoData;
    payment?: Payment;
    trash?: boolean;
};

type BoletosResponse = {
    rows: BoletoItem[];
    count: number;
    page: number;
    pages: number;
};

type FetchParams = {
    page: number;
    rows: number;
};

type Props = {
    item: BonoData;
};

export default function BonoBoletos({ item }: Props): React.ReactElement {
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();

    const [boletos, setBoletos, , Get] = useCrud<BoletosResponse>();
    const [viewBoleto, setViewBoleto] = useState<BoletoItem | undefined>();
    const [filters, setFilter] = useTuples({
        userName: '',
    });
    const [selectedMshipId, setSelectedMshipId] = useState<number | null>(null);

    const [trash, toggleTrash] = useToggle(() => {
        loadBoletos();
    });

    const [isActive, toggleActive] = useToggle(() => {
        loadBoletos();
    }, false);

    const loadBoletos = useCallback(
        (params: FetchParams = { page: 1, rows: 20 }): void => {
            if (typeof item.id === 'number') {
                Get(`/bonos/${item.id}/boletos`, {
                    ...params,
                    ...filters,
                    trash,
                    isActive,
                }).then((response) => {
                    const data = response as BoletosResponse;
                    data.rows.sort((a, b) =>
                        (b.created ?? '').localeCompare(a.created ?? ''),
                    );
                    setBoletos(data);
                });
            }
        },
        [Get, item.id, filters, trash, isActive, setBoletos],
    );

    const downloadPdf = (): void => {
        downloadFile(
            `/bonos/${item.id}/boletos.pdf`,
            'application/pdf',
            { ...filters, trash, isActive },
            `${item.name}_${t('Users')}_${format(new Date(), 'dd-MM-yyyy')}.pdf`,
        );
    };

    const downloadCsv = (): void => {
        downloadFile(
            `/bonos/${item.id}/boletos.csv`,
            'text/csv',
            { ...filters, trash, isActive },
            `${item.name}_${t('Users')}_${format(new Date(), 'dd-MM-yyyy')}.csv`,
        );
    };

    const openViewNewBoleto = (): void => {
        setViewBoleto({
            bono: item,
        } as BoletoItem);
    };

    return (
        <>
            <SttTopControls
                p={2}
                fields={[
                    {
                        caption: t('Name'),
                        type: 'search',
                        name: 'userName',
                        value: filters.userName,
                        onEnterKey: () => loadBoletos(),
                        xs: 12,
                        sm: 12,
                        md: 6,
                        lg: 6,
                    },
                    {
                        caption: t('Type'),
                        type: 'select',
                        name: 'isActive',
                        value: (+isActive).toString(),
                        mandatory: false,
                        cleanable: false,
                        options: [
                            {
                                label: t('All'),
                                value: 0,
                            },
                            {
                                label: t('Active'),
                                value: 1,
                            },
                        ],
                        onChange: ({
                            value,
                        }: {
                            name: string | undefined;
                            value: unknown;
                        }) => toggleActive(!!parseInt(value as string)),
                        xs: 12,
                        sm: 12,
                        md: 6,
                        lg: 6,
                    },
                ]}
                menu={[
                    {
                        caption: `${t('Download')} PDF`,
                        onClick: downloadPdf,
                    },
                    {
                        caption: `${t('Download')} CSV`,
                        onClick: downloadCsv,
                    },
                ]}
                menuIcon={<CloudDownload />}
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.VOUCHER.MODIFY,
                    onClick: openViewNewBoleto,
                }}
                onToggleTrash={toggleTrash}
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: unknown;
                }) => {
                    if (!name) return;
                    setFilter({
                        name: name as 'userName',
                        value: value as string,
                    });
                }}
                onFilter={loadBoletos}
            />
            <SttTable
                onFetch={loadBoletos}
                data={boletos}
                columns={[
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        align: 'left',
                        value: (row: BoletoItem) => (
                            <Link
                                onClick={() => setViewBoleto(row)}
                                variant="body2"
                                sx={{
                                    color: 'var(--brand-text)',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                {row.user ? fullName(row.user) : t('NoUser')}
                            </Link>
                        ),
                    },
                    {
                        title: t('Active'),
                        type: 'binaryStatus',
                        value: (row: BoletoItem) => {
                            const now = new Date();
                            const expirationDate = row.expiration
                                ? parseISO(
                                      `${format(parseISO(row.expiration), 'yyyy-MM-dd')}T${item.timeend}`,
                                  )
                                : null;
                            const bonoEndDate = parseISO(
                                `${row.bono.dateend}T${item.timeend}`,
                            );

                            return (
                                row.consumed < row.amount &&
                                row.amount + row.consumed > 0 &&
                                (!expirationDate ||
                                    isBefore(now, expirationDate)) &&
                                isBefore(now, bonoEndDate)
                            );
                        },
                    },
                    {
                        title: t('Consumed'),
                        type: 'text',
                        field: 'consumed',
                    },
                    {
                        title: t('Total'),
                        type: 'text',
                        field: 'amount',
                    },
                    {
                        title: t('Expiration'),
                        type: 'dmy',
                        field: 'expiration',
                    },
                    {
                        title: t('PaymentForm'),
                        type: 'pf',
                        value: (row: BoletoItem) =>
                            row.payment
                                ? getPaymentForm({
                                      paymentForm: row.payment.paymentForm,
                                      trash: row.payment.trash,
                                  })
                                : '',
                    },
                    {
                        title: t('Price'),
                        align: 'right',
                        type: 'price',
                        value: (row: BoletoItem) =>
                            row.payment ? (
                                <SttLabelPayment
                                    paymentDialogActive
                                    payment={row.payment}
                                    onClose={loadBoletos}
                                />
                            ) : (
                                '-'
                            ),
                    },
                    {
                        title: '',
                        value: (row: BoletoItem) =>
                            row.user?.mship?.id ? (
                                <PersonOutlineOutlinedIcon
                                    fontSize="small"
                                    sx={{ cursor: 'pointer' }}
                                    aria-label={t('ViewMembership')}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                        setSelectedMshipId(row.user?.mship?.id)
                                    }
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            setSelectedMshipId(
                                                row.user?.mship?.id,
                                            );
                                        }
                                    }}
                                />
                            ) : null,
                    },
                ]}
            />

            {viewBoleto && (
                <BoletoDialog
                    item={{
                        id: viewBoleto.id,
                        user: viewBoleto.user,
                        bono:
                            typeof viewBoleto.bono.id === 'number'
                                ? {
                                      id: viewBoleto.bono.id,
                                      name: viewBoleto.bono.name,
                                      price: viewBoleto.bono.price || 0,
                                  }
                                : undefined,
                        payment: viewBoleto.payment,
                        expiration: viewBoleto.expiration,
                        consumed: viewBoleto.consumed,
                        amount: viewBoleto.amount,
                        notes: viewBoleto.notes,
                        trash: viewBoleto.trash,
                    }}
                    onSave={loadBoletos}
                    onClose={() => {
                        setViewBoleto(undefined);
                        loadBoletos();
                    }}
                />
            )}

            <SttMshipDialog
                open={selectedMshipId !== null}
                mshipId={selectedMshipId}
                onClose={() => {
                    setSelectedMshipId(null);
                    loadBoletos();
                }}
            />
        </>
    );
}
